import { Badge, Box, Container, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { IconUsers } from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { usePortalSubject } from '../../../contexts/PortalSubjectContext';
import { LocalhostOnlyBadge } from '../../../components/LocalhostOnlyBadge';

interface ClientPortalLayoutProps {
  title: string;
  description?: string;
  children: ReactNode;
  rightHeaderSlot?: ReactNode;
  /** When false, omits the page title header card (e.g. form fill uses template title inside the form). */
  showPageHeader?: boolean;
  /** For pages that need full viewport width (e.g. chat). */
  fullBleed?: boolean;
  /** FEATURE_VISIBILITY-gated pages show the localhost badge next to the title. */
  localhostOnly?: boolean;
}

function LayoutInner({
  title,
  description,
  children,
  rightHeaderSlot,
  showPageHeader = true,
  fullBleed,
  localhostOnly,
}: ClientPortalLayoutProps): JSX.Element {
  const { activeAccount } = usePortalSubject();
  const showBanner = activeAccount && !activeAccount.isSelf;

  return (
    <Box py="lg">
      <Container size="xl">
        {showBanner && (
          <Paper p="sm" mb="sm" withBorder style={{ backgroundColor: 'var(--mantine-color-blue-0)', borderColor: 'var(--mantine-color-blue-3)' }}>
            <Group gap="xs" wrap="nowrap">
              <IconUsers size={16} stroke={1.5} style={{ color: 'var(--mantine-color-blue-6)' }} />
              <Text size="sm" c="blue.7" fw={500}>
                Viewing portal for {activeAccount.label}
              </Text>
              <Badge variant="light" color="blue" size="xs">Guarantor View</Badge>
            </Group>
          </Paper>
        )}
        {showPageHeader && (
          <Paper p="md" withBorder>
            <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
              <div style={{ minWidth: 0 }}>
                <Group gap="sm" align="center" wrap="wrap">
                  <Title order={2} style={{ letterSpacing: '-0.01em' }}>
                    {title}
                  </Title>
                  {localhostOnly ? <LocalhostOnlyBadge /> : null}
                </Group>
                {description && (
                  <Text size="sm" c="gray.6" mt={4}>
                    {description}
                  </Text>
                )}
              </div>
              <Group gap="md" wrap="nowrap">{rightHeaderSlot}</Group>
            </Group>
          </Paper>
        )}
      </Container>

      {fullBleed ? (
        <Box mt={showPageHeader ? 'lg' : undefined}>{children}</Box>
      ) : (
        <Container size="xl">
          <Stack gap="lg" mt={showPageHeader ? 'lg' : undefined}>
            {children}
          </Stack>
        </Container>
      )}
    </Box>
  );
}

export function ClientPortalLayout(props: ClientPortalLayoutProps): JSX.Element {
  return <LayoutInner {...props} />;
}

